import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { combineLatest, distinctUntilChanged, filter, finalize, Subject, takeUntil } from 'rxjs';
import { IAccount } from '@services/accounts.service';
import { IOrganisationsHierarchy, OrganisationDataService, OrganisationsHierarchy } from '@services/organisation-data.service';

@Component({
	selector: 'app-dashboard-flagged-assets',
	templateUrl: './dashboard-flagged-assets.component.html',
	styleUrls: ['./dashboard-flagged-assets.component.scss']
})
export class DashboardFlaggedAssetsComponent implements OnDestroy {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** ruleEvents */
	public flaggedAssets: any[] = [];

	/** fields */
	public fields: Object = { text: 'Name', id: 'AssetID' };

	/** accountID */
	@Input() accountID: string;
	/** selectedAccount */
	public selectedAccount: IAccount;
	/** OrganisationID */
	@Input() public OrganisationUID: string;

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[];

	public locationUID: string = "";

	/** noWatchList */
	@Output() noWatchList = new EventEmitter<boolean>();

	constructor(
		public assetsDataService: AssetsDataService,
		public authenticationService: AuthenticationService,
		public organisationDataService: OrganisationDataService,
		private router: Router,
		public siteSubscriptions: siteSubscriptions,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions
	) {
		this.globalFunctions.displaySpinner(true)
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.hierarchicalData$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.queryParamMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, selectedOrganisationUID, hierarchicalData, queryParamMap]) =>
				!!selectedAccountGUID && !!queryParamMap && !!hierarchicalData && !!selectedOrganisationUID)
		).subscribe({
			next: ([selectedAccountGUID, selectedOrganisationUID, hierarchicalData, queryParamMap]) => {
				this.accountID = selectedAccountGUID;
				this.OrganisationUID = selectedOrganisationUID;

				this.hierarchy = JSON.parse(hierarchicalData);

				this.getFlaggedAssets();
				// this.subscribeOrganisationStructure(this.accountID, this.OrganisationUID)
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * getWatchListAssets
	 */
	getFlaggedAssets() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.getFlaggedAssets$(this.accountID, this.OrganisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((assets) => !!assets),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (flagged: any) => {
				let list: LevelAsset[] = this.flattenAssets(this.hierarchy);
				let listAssetIDs: number[] = [];
				list.forEach(asset => {
					listAssetIDs.push(asset.AssetID);
				});
				let parsedFlagged: any[] = [];
				if (flagged[0].Data) {
					parsedFlagged = JSON.parse(flagged[0].Data);
				}
				this.flaggedAssets = parsedFlagged.filter((x: any) => listAssetIDs.includes(x.AssetID));
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.assetsDataService.getFlaggedAssets$", false);
				}
			}
		});
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/** goToFunctionalLocation */
	goToFunctionalLocation(asset: LevelAsset) {
		this.siteSubscriptions.SetSelectedAsset(asset);
		this.router.navigate([`/${this.accountID}/organisation/${this.OrganisationUID}/location/${asset.ParentUID}/asset/${asset.AssetID}`]);
	}

	/**
	 * goToFlagged
	 * @param asset asset 
	 */
	goToFlagged(asset: LevelAsset) {
		this.siteSubscriptions.SetSelectedAsset(asset);
		this.router.navigate([`${this.accountID}/organisation/${this.OrganisationUID}/flagged`])
	}
}
